"use client";
import useTranslate from "@app/_hooks/useTranslate";
import { Input } from "@components/inputs";
import { Button } from "@components/ngg";
import { useAuth } from "@context/authContext";
import { useMarket } from "@context/marketContext";
import type { AuthRegisterAccountDtoInput } from "@server/gql/graphql";
import type { FormEvent } from "react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { createFieldError } from "@/utils/common";
import {
  passwordMinLength,
  passwordRules,
  passwordValidationRegex,
} from "@/utils/passwordRegex";

import ToolTip from "./formPasswordStrengthToolTip";

export default function RegistrationForm({
  className
}: {
  className?: string;
}) {
  const t = useTranslate();
  const [validationError, setValidationError] = useState({
    personFirstName: "",
    personLastName: "",
    email: "",
    password: "",
    repeatPassword: "",
    companyName: "",
    companyOrganizationalNumber: "",
    phoneNumber: "",
  });
  const { market } = useMarket().state;
  const { auth, register } = useAuth();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { serviceLocale: locale } = market;
    const formData = new FormData(event.currentTarget);
    const password = formData.get("password")?.toString();
    const repeatPassword = formData.get("repeatPassword")?.toString();

    if (!password) return;
    if (password !== repeatPassword) {
      setValidationError((old) => ({
        ...old,
        repeatPassword: "errorPasswordsDoNotMatch",
      }));
      return;
    }
    if (!passwordValidationRegex.test(password)) {
      setValidationError((old) => ({
        ...old,
        repeatPassword: "errorPasswordTooWeak",
      }));
      return;
    }

    formData.delete("repeatPassword");
    const payload: AuthRegisterAccountDtoInput = {
      ...Object.fromEntries(formData),
      isCompany: true,
      locale,
      countryCode: market.code,
      brand: "NG",
    };

    try {
      await register(payload, password);
    } catch (e: any) {
      setValidationError((old) => createFieldError<any>(old, e));
    }
  };

  return (
    <form
      action=""
      onSubmit={handleSubmit}
      className={cn("w grid grid-cols-1 gap-3 sm:grid-cols-2", className)}
    >
      <Input
        name="companyName"
        id="companyName"
        type="text"
        required
        label={t("formCompanyNameLabel")}
        aria-invalid={!!validationError.companyName}
        hint={t(validationError.companyName)}
      />
      <Input
        name="companyOrganizationalNumber"
        id="companyOrganizationalNumber"
        type="text"
        required
        label={t("formCompanyOrganizationalNumberLabel")}
        aria-invalid={!!validationError.companyOrganizationalNumber}
        hint={t(validationError.companyOrganizationalNumber)}
      />
      <Input
        name="personFirstName"
        id="personFirstName"
        type="text"
        required
        label={t("formFirstNameLabel")}
        aria-invalid={!!validationError.personFirstName}
        hint={t(validationError.personFirstName)}
      />
      <Input
        name="personLastName"
        id="personLastName"
        type="text"
        required
        label={t("formLastNameLabel")}
        aria-invalid={!!validationError.personLastName}
        hint={t(validationError.personLastName)}
      />
      <Input
        name="phoneNumber"
        id="phoneNumber"
        type="text"
        required
        label={t("formPhoneNumberLabel")}
        aria-invalid={!!validationError.phoneNumber}
        hint={t(validationError.phoneNumber)}
      />
      <Input
        name="email"
        id="email"
        type="email"
        required
        label={t("formEmailLabel")}
        aria-invalid={!!validationError.email}
        hint={t(validationError.email)}
      />
      <Input
        name="password"
        id="password"
        type="password"
        label={t("formPasswordLabel")}
        hint="."
        required
        aria-invalid={!!validationError.password}
        HintComponent={() =>
          ToolTip({
            open: isTooltipOpen,
            setOpen: setIsTooltipOpen
          })
        }
        autoComplete="new-password"
        minLength={passwordMinLength}
        passwordrules={passwordRules}
      />
      <Input
        name="repeatPassword"
        id="repeatPassword"
        type="password"
        label={t("formRepeatPasswordLabel")}
        aria-invalid={
          !!validationError.repeatPassword || !!validationError.password
        }
        required
        hint={
          t(
            validationError.repeatPassword || validationError.password
          )
        }
        autoComplete="new-password"
        minLength={passwordMinLength}
        passwordrules={passwordRules}
      />
      <div className="col-span-full mt-6 max-w-[356px] place-self-center">
        <Button
          type="submit"
          className="btn-primary"
          isLoading={auth?.loading}
          disabled={auth?.loading}
          fullWidth
        >
          {t("createAccount")}
        </Button>
        <p className="mt-3 text-sm text-grey-300">
          {t("registerFormTermsText")}
        </p>
      </div>
    </form>
  );
}
